/** Thrown when an awaited call did not settle within its budget. */
export class TimeoutError extends Error {
  constructor(label: string, ms: number) {
    super(`${label} timed out after ${ms}ms`);
    this.name = "TimeoutError";
  }
}

/**
 * Reject `promise` if it hasn't settled within `ms`.
 *
 * `fetch` (and therefore every Supabase call) has no timeout of its own: when
 * the database stops accepting connections the request simply never settles.
 * A query built on such a call stays "loading" forever — which is how an
 * unreachable backend turned into a spinner that no refresh could clear.
 *
 * The timer is cleared once the promise settles, so a resolved call leaves no
 * pending timeout behind.
 */
export function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new TimeoutError(label, ms)), ms);
  });

  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer)) as Promise<T>;
}
